/* Sunpura mega menu: panels, native mobile accordions and one-shot ring motion. */
(() => {
  window.__sunpuraMegaMenuDestroy?.();
  const app = new AbortController();
  const signal = app.signal;
  const bindings = new Map();
  const running = new Map();
  const desktop = matchMedia('(min-width: 992px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const cardSelector = '.sp-mega-menu--panels .sp-mm-native-card[href]';
  const normalize = (text = '') => text.toString().trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
  const within = (scope, selector) => [ ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector) ];

  function stop(card) {
    const state = running.get(card);
    if (!state) return;
    cancelAnimationFrame(state.frame);
    state.ring.style.removeProperty('--sp-mm-ring-angle');
    running.delete(card);
  }
  function stopWithin(scope) {
    for (const card of running.keys()) if (scope === document || scope.contains(card)) stop(card);
  }
  function run(card) {
    if (!desktop.matches || reduced.matches || document.hidden || running.has(card)) return;
    const ring = card.querySelector('.sp-mm-native-card__energy-head');
    if (!ring) return;
    const started = performance.now();
    const state = {ring, frame: 0};
    running.set(card, state);
    const tick = (now) => {
      if (!card.isConnected || !card.getClientRects().length) { stop(card); return; }
      const t = Math.min(1, (now - started) / 1120);
      const ease = t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      ring.style.setProperty('--sp-mm-ring-angle', `${225 + 360 * ease}deg`);
      if (t < 1) state.frame = requestAnimationFrame(tick);
      else stop(card); // 585deg and the static 225deg give the same final ring.
    };
    state.frame = requestAnimationFrame(tick);
  }

  function bindDesktop(menu) {
    if (bindings.has(menu)) return;
    const triggers = [...menu.querySelectorAll('.menu-sunpura_mega_panels .mega-menu__link--level-2')];
    const panels = [...menu.querySelectorAll('[data-sp-mm-panel]')];
    const flat = menu.classList.contains('sp-mega-menu--flat');
    if (!panels.length || (!flat && !triggers.length)) return;
    const controller = new AbortController();
    const options = {signal: controller.signal};
    const byLabel = new Map();
    const byResource = new Map();
    panels.forEach((panel, index) => {
      if (!panel.id) panel.id = `${menu.id}-sunpura-panel-${index}`;
      if (panel.dataset.spMmResource) byResource.set(panel.dataset.spMmResource, panel);
      const label = normalize(panel.dataset.spMmTrigger);
      if (label && !byLabel.has(label)) byLabel.set(label, panel);
    });
    const targets = new Map(triggers.map(trigger => [trigger, byResource.get(trigger.dataset.spMmResource) || byLabel.get(normalize(trigger.textContent))]));
    triggers.forEach(trigger => {
      trigger.removeAttribute('aria-selected');
      const panel = targets.get(trigger);
      if (panel && !flat) {
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('role', 'button');
      }
    });
    let active;
    const show = (trigger) => {
      const matched = targets.get(trigger);
      // Unmatched navigation must never blank the last valid panel.
      if (!matched || active === trigger) return;
      panels.forEach(panel => {
        panel.hidden = panel !== matched;
        if (panel.hidden) stopWithin(panel);
      });
      triggers.forEach(candidate => {
        const selected = Boolean(matched) && candidate === trigger;
        candidate.classList.toggle('sp-mm-trigger--active', selected);
        if (targets.get(candidate)) candidate.setAttribute('aria-expanded', String(selected));
      });
      active = trigger;
    };
    const showFlat = () => panels.forEach(panel => { panel.hidden = false; });
    const controls = flat ? [] : triggers;
    controls.forEach((trigger, index) => {
      trigger.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') show(trigger); }, options);
      trigger.addEventListener('focus', () => show(trigger), options);
      trigger.addEventListener('click', event => {
        if (!targets.get(trigger)) return;
        event.preventDefault(); show(trigger);
      }, options);
      trigger.addEventListener('keydown', event => {
        if (event.key === ' ' && targets.get(trigger)) {
          event.preventDefault(); show(trigger); return;
        }
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % triggers.length;
        if (event.key === 'ArrowUp') next = (index + triggers.length - 1) % triggers.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = triggers.length - 1;
        if (next !== undefined) { event.preventDefault(); triggers[next].focus(); }
        if (event.key === 'ArrowRight') {
          const first = targets.get(trigger)?.querySelector('a[href]');
          if (first) { event.preventDefault(); first.focus(); }
        }
      }, options);
    });
    if (flat) showFlat();
    else show(triggers.find(trigger => targets.get(trigger) && trigger.getAttribute('aria-current') === 'page') || triggers.find(trigger => targets.get(trigger)));
    menu.addEventListener('toggle', event => {
      if (event.target !== menu) return;
      if (!menu.open) stopWithin(menu);
      else if (flat) showFlat();
      menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(menu.open));
    }, options);
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault(); event.stopPropagation(); menu.open = false;
        menu.querySelector(':scope > summary')?.focus();
      }
    }, options);
    bindings.set(menu, {controller, select(blockId) {
      const panel = panels.find(item => item.dataset.spMmBlock === blockId);
      const trigger = triggers.find(item => targets.get(item) === panel);
      if (!panel || (!flat && !trigger)) return;
      menu.open = true;
      if (flat) showFlat();
      else show(trigger);
    }});
  }

  function bindHover(header) {
    if (bindings.has(header) || header.dataset.maindrop !== 'mousehover') return;
    const menus = [...header.querySelectorAll('header-menu > details')];
    const controller = new AbortController();
    const options = {signal: controller.signal};
    let closeTimer;
    let order = 0;
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('c-scroll-trigger--offscreen');
        if (entry.target.hasAttribute('data-cascade')) entry.target.style.setProperty('--animation-order', order++);
      });
    }, {rootMargin: '0px 0px -50px 0px'}) : null;
    const close = menu => {
      menu.open = false;
      menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      stopWithin(menu);
    };
    menus.forEach(menu => {
      menu.querySelectorAll('.c-scroll-trigger').forEach(el => observer?.observe(el));
      menu.addEventListener('pointerenter', event => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        clearTimeout(closeTimer);
        menus.forEach(other => { if (other !== menu && other.open) close(other); });
        if (!menu.open) {
          order = 0;
          menu.querySelectorAll('.c-scroll-trigger').forEach(el => {
            if (observer) el.classList.add('c-scroll-trigger--offscreen');
            else el.classList.remove('c-scroll-trigger--offscreen');
          });
        }
        menu.open = true;
        menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
      }, options);
      menu.addEventListener('pointerleave', event => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        clearTimeout(closeTimer);
        closeTimer = setTimeout(() => {
          if (!menu.contains(document.activeElement)) close(menu);
        }, 200);
      }, options);
    });
    controller.signal.addEventListener('abort', () => { clearTimeout(closeTimer); observer?.disconnect(); }, {once:true});
    bindings.set(header, {controller});
  }

  function bindMobile(drawer) {
    if (bindings.has(drawer)) return;
    const root = drawer.querySelector(':scope > details');
    if (!root) return;
    const controller = new AbortController();
    const options = {signal: controller.signal};
    const groups = [...drawer.querySelectorAll('[data-sp-mm-group]')].map(element => ({
      element,
      button: element.querySelector('[data-sp-mm-toggle]'),
      panel: element.querySelector(':scope > .sp-mm-mobile-panel')
    })).filter(group => group.button && group.panel);
    const set = (group, expanded) => {
      group.button.setAttribute('aria-expanded', String(expanded));
      group.panel.hidden = !expanded;
    };
    const reset = () => groups.forEach(group => set(group, group.element.dataset.spMmDefaultOpen === 'true'));
    reset();
    groups.forEach(group => group.button.addEventListener('click', () => {
      const expand = group.button.getAttribute('aria-expanded') !== 'true';
      const list = group.element.closest('.sp-mm-mobile-parent');
      groups.forEach(other => {
        if (other.element.closest('.sp-mm-mobile-parent') === list) set(other, other === group && expand);
      });
      const category = group.element.closest('.menu-drawer__submenu');
      if (category && typeof trapFocus === 'function') trapFocus(category, group.button, {preventScroll:true});
    }, options));
    root.addEventListener('toggle', event => {
      if (event.target !== root) return;
      if (!root.open) reset();
    }, options);
    controller.signal.addEventListener('abort', () => {
      if (root.open) drawer.closeMenuDrawer?.(new Event('sectionunload'), root.querySelector(':scope > summary'));
    }, {once:true});
    // No capture listeners, propagation interception or second Escape handler.
    bindings.set(drawer, {controller});
  }

  function init(scope = document) {
    within(scope, '.header-wrapper.header-wrapper-line').forEach(bindHover);
    within(scope, '.sp-mega-menu--panels').forEach(bindDesktop);
    within(scope, 'header-drawer[data-sp-mm-drawer]').forEach(bindMobile);
  }
  function unload(scope) {
    stopWithin(scope);
    for (const [element, binding] of bindings) {
      if (scope === document || scope === element || scope.contains(element)) {
        binding.controller.abort(); bindings.delete(element);
      }
    }
  }
  for (const type of ['pointerover', 'focusin']) document.addEventListener(type, event => {
    const card = event.target.closest?.(cardSelector);
    if (!card || card.contains(event.relatedTarget) || event.pointerType === 'touch') return;
    run(card);
  }, {signal});
  for (const type of ['pointerout', 'focusout']) document.addEventListener(type, event => {
    const card = event.target.closest?.(cardSelector);
    if (!card || card.contains(event.relatedTarget)) return;
    if (!card.matches(':hover, :focus-visible')) stop(card);
  }, {signal});
  desktop.addEventListener('change', () => {
    stopWithin(document);
    if (!desktop.matches) {
      document.querySelectorAll('header-menu > details[open]').forEach(menu => {
        menu.open = false;
        menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
      });
    } else {
      document.querySelectorAll('header-drawer[data-sp-mm-drawer]').forEach(drawer => {
        const root = drawer.querySelector(':scope > details');
        if (root?.open) drawer.closeMenuDrawer?.(new Event('breakpointchange'), root.querySelector(':scope > summary'));
      });
    }
  }, {signal});
  reduced.addEventListener('change', () => stopWithin(document), {signal});
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopWithin(document); }, {signal});
  document.addEventListener('shopify:section:load', event => init(event.target), {signal});
  document.addEventListener('shopify:section:unload', event => unload(event.target), {signal});
  document.addEventListener('shopify:block:select', event => {
    for (const binding of bindings.values()) binding.select?.(event.detail?.blockId);
  }, {signal});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true, signal});
  else init();
  window.__sunpuraMegaMenuDestroy = () => { unload(document); app.abort(); };
})();
